import useSWR from 'swr'

import { apiClient } from 'common/api'
import { serverless } from 'common/sdk'
import { session } from 'auth/session'
import { useOrgNameInUrl } from './useOrgNameInUrl'

const me = session.get('user')

export const useOrgName = () => {
  /**
   * Determine what orgName is in the URL
   *
   * Note: we can't use the react-router hook useParams(), because
   * this hook may be called outside of a <Route /> tree, which
   * will apparently return an empty object.
   *
   * See: https://github.com/ReactTraining/react-router/issues/7026
   */

  /**
   * Grab the orgName from the URL, if there is one there
   */
  const orgNameInUrl = useOrgNameInUrl()

  /**
   * __Conditionally__ fetch your orgs, because we need to see if
   * we might need to fallback to loading an org if there's nothing
   * in the URL (signing up, landing on a bare '/', etc.)
   */
  const { data: orgs } = useSWR(
    /**
     * Only fetch if the org in the URL is not present
     */
    () => (me && !orgNameInUrl ? 'core/tenants' : null),
    (url) =>
      apiClient({
        url,
        params: {
          userName: me.userName,
        },
      }),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  )

  /**
   * Return the org that's in the URL, if it's there
   *
   * This should be the end the function in most cases. We only precede
   * if you land on the bare /, and need to figure out where you should
   * be redirected to next.
   */
  if (orgNameInUrl) {
    serverless.config({
      context: {
        orgName: orgNameInUrl,
      },
    })
    return orgNameInUrl
  }

  /**
   * If you switched tenants it was saved to local storage, go to this org
   */
  const preferredOrgName = session.get('selectedOrgName')
  if (preferredOrgName) {
    serverless.config({
      context: {
        orgName: preferredOrgName,
      },
    })
    return preferredOrgName
  }

  /**
   * Not entirely sure what this field is, but try to take your username
   * and match it against an org 'title' - they seem to be the same. If
   * you're an owner of the org, then it's likely your org. Remember, this
   * will only happen if params.orgName (nothing in the URL) occurs, and
   * we're trying our best to select a reasonable org you're an owner of.
   */
  let ownerOfOrg = orgs?.find((org) => org.role === 'owner' && org.title === me?.userName)

  if (ownerOfOrg) {
    serverless.config({
      context: {
        orgName: ownerOfOrg.orgName,
      },
    })
    return ownerOfOrg.orgName
  }

  /**
   * Fallback, pick another org you're an owner of
   */
  ownerOfOrg = orgs?.find((org) => org.role === 'owner')

  if (ownerOfOrg) {
    serverless.config({
      context: {
        orgName: ownerOfOrg.orgName,
      },
    })
    return ownerOfOrg.orgName
  }

  /**
   * If there's some weird case where you're not an owner, just pick the first org
   */
  serverless.config({
    context: {
      orgName: orgs?.[0]?.orgName,
    },
  })
  return orgs?.[0]?.orgName
}
